import { action } from "typesafe-actions";
import { ThunkAction } from "redux-thunk";
import { AnyAction } from "redux";
import { api } from "../../api/api";
import { IStoreState } from "../initialStoreState";
import {
  saveLoaderCompleted,
  saveLoaderProgress,
  showMessage,
} from "../messages/messagesActions";
import { ICatalogProduct } from "./CatalogProducts.types";
import { parseMaybeJson } from "../../helpers/parseMaybeJson";
import {
  uploadFile,
  uploadMultipleFile,
} from "../../components/FileUpload/utils";
import { IFileUpload } from "../../components/FileUpload/FileUpload.type";

// Action type constants
export const FETCH_CATALOG_PRODUCTS_PROGRESS =
  "FETCH_CATALOG_PRODUCTS_PROGRESS";
export const FETCH_CATALOG_PRODUCTS_SUCCESS =
  "FETCH_CATALOG_PRODUCTS_SUCCESS";
export const FETCH_CATALOG_PRODUCTS_FAILED = "FETCH_CATALOG_PRODUCTS_FAILED";
export const CLEAR_CATALOG_PRODUCTS_STATE = "CLEAR_CATALOG_PRODUCTS_STATE";

// Action creators
export const fetchCatalogProductsProgress = () =>
  action(FETCH_CATALOG_PRODUCTS_PROGRESS);
export const fetchCatalogProductsSuccess = (
  data: ICatalogProduct[],
  totalRecords: number,
) => action(FETCH_CATALOG_PRODUCTS_SUCCESS, { data, totalRecords });
export const fetchCatalogProductsFailed = () =>
  action(FETCH_CATALOG_PRODUCTS_FAILED);
export const clearCatalogProductsState = () =>
  action(CLEAR_CATALOG_PRODUCTS_STATE);

// Async thunks
export const fetchCatalogProductsAsync =
  (
    internalCatalogsUuid: string,
    pageNo: number = 1,
    itemPerPage: number = 20,
    status: string = "ACTIVE",
    searchValue?: string,
    companyOutletUuid?: string,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    dispatch(fetchCatalogProductsProgress());
    dispatch(saveLoaderProgress());
    try {
      let url = `/genai/get-catalog-products?internal_catalogs_uuid=${internalCatalogsUuid}&status=${status}&pageNo=${pageNo}&itemPerPage=${itemPerPage}`;

      if (companyOutletUuid) {
        url += `&company_outlet_uuid=${companyOutletUuid}`;
      }
      if (searchValue) {
        url += `&columns=${JSON.stringify(["name", "retailer_id", "category", "brand"])}&value=${encodeURIComponent(searchValue)}`;
      }

      const response = await api.get(url);
      const rawData: ICatalogProduct[] = response.data.data || [];
      const data = rawData.map((product) => ({
        ...product,
        additional_image_urls: parseMaybeJson<string[]>(
          product.additional_image_urls as any,
          [],
        ),
      }));
      const totalRecords = response.data.totalRecords || 0;
      dispatch(fetchCatalogProductsSuccess(data, totalRecords));
    } catch (err: any) {
      dispatch(fetchCatalogProductsFailed());
      dispatch(
        showMessage({
          type: "error",
          message:
            err.response?.data?.message || "Failed to fetch catalog products",
          displayAs: "snackbar",
        }),
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const upsertCatalogProductAsync =
  (
    data: ICatalogProduct,
    onCallback: (isSuccess: boolean, data?: ICatalogProduct) => void,
    fileData?: {
      mainImageFile: File | null;
      mainImagePath: string;
      additionalImages: IFileUpload[];
      companyOutletUuid: string;
    },
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    dispatch(saveLoaderProgress());
    try {
      let finalPayload = { ...data };

      // Handle file uploads if file data is provided
      if (fileData) {
        // Upload main image if a new file was selected
        let finalImageUrl = fileData.mainImagePath;
        if (fileData.mainImageFile) {
          finalImageUrl = await uploadFile({
            file: fileData.mainImageFile,
            module_name: "WHATSAPP_MARKET_CAMPAIGN",
            defaultReturn: fileData.mainImagePath,
            asPayload: {
              company_outlet_uuid: fileData.companyOutletUuid,
            },
          });
        }

        // Upload additional images (new files)
        let finalAdditionalUrls: string[] = [];
        if (fileData.additionalImages.length > 0) {
          const uploadResult = await uploadMultipleFile(
            fileData.additionalImages,
            "WHATSAPP_MARKET_CAMPAIGN",
            { company_outlet_uuid: fileData.companyOutletUuid },
          );
          finalAdditionalUrls = (uploadResult.paths || []).filter(
            (p): p is string => p !== null && p.trim() !== "",
          );
        }

        finalPayload.image_url = finalImageUrl;
        finalPayload.additional_image_urls = finalAdditionalUrls;
      }

      const response = await api.post("/genai/create-catalog-products", finalPayload);
      dispatch(
        showMessage({
          type: "success",
          message:
            response.data.message || "Catalog product created successfully",
          displayAs: "snackbar",
        }),
      );
      onCallback(true, response.data.data);
    } catch (err: any) {
      dispatch(
        showMessage({
          type: "error",
          message:
            err.response?.data?.message || "Failed to save catalog product",
          displayAs: "snackbar",
        }),
      );
      onCallback(false);
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const deleteCatalogProductAsync =
  (
    catalogProductUuid: string,
    companyOutletUuid: string,
    onCallback: (isSuccess: boolean) => void,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    dispatch(saveLoaderProgress());
    try {
      const response = await api.get(
        `/genai/delete-meta-catalog-products?catalog_product_uuid=${catalogProductUuid}&company_outlet_uuid=${companyOutletUuid}`,
      );
      dispatch(
        showMessage({
          type: "success",
          message: "Product deleted successfully",
          displayAs: "snackbar",
        }),
      );
      onCallback(true);
    } catch (err: any) {
      dispatch(
        showMessage({
          type: "error",
          message:
            err.response?.data?.message || "Failed to delete catalog product",
          displayAs: "snackbar",
        }),
      );
      onCallback(false);
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };
